import React, { useState } from 'react';
import styled from 'styled-components/macro';
import { Body, Button, Flex, Modal, Title } from '@cognite/cogs.js';
import { notification } from 'antd';
import { Access, useDeleteAccessMutation } from 'types/dt-graphql-apollo-v2';
import mixpanel from 'mixpanel-browser';
import { useUrl } from 'hooks/useUrls';

export const DeleteMemberModal = ({
  visible,
  selectedUserAccess,
  onSuccess,
  onCancel,
}: {
  visible: boolean;
  selectedUserAccess: Access | null;
  onSuccess: VoidFunction;
  onCancel: VoidFunction;
}) => {
  const tenant = useUrl.Tenant();
  const [loading, setLoading] = useState(false);
  const [deleteAccess] = useDeleteAccessMutation();

  const handleAccessDeletion = async () => {
    if (!selectedUserAccess) return;

    setLoading(true);
    try {
      await deleteAccess({
        variables: {
          id: selectedUserAccess.externalId,
        },
      });

      mixpanel.track(
        `Configured Access Control and deleted the privileges for one user.`,
        {
          tenant,
          role: selectedUserAccess.role,
        }
      );
      notification.success({
        message: `Access was deleted.`,
        description: `Successfully removed the privileges for the user ${selectedUserAccess.userId}.`,
      });
      onSuccess();
    } catch (err: any) {
      notification.error({
        message: `Error while deleting the privileges for the user.`,
        description: err,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <StyledModal visible={visible} onCancel={onCancel}>
      <Flex direction="column" gap={16} style={{ marginBottom: '4rem' }}>
        <Title level={3}>Delete privileged access</Title>
        <Body level={2}>
          Deleting the privileged user access will remove the user from this
          list and remove any Editor or Administrator rights. If the user is
          added to the identity provider service of your company, the user still
          have Reader access.
        </Body>
        <Body level={2}>
          Are you sure you want to delete the access for this user?
        </Body>
      </Flex>

      <Flex gap={10} justifyContent="flex-end">
        <Button type="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          type="destructive"
          loading={loading}
          onClick={handleAccessDeletion}
        >
          Delete
        </Button>
      </Flex>
    </StyledModal>
  );
};

const StyledModal = styled(Modal)`
  width: 600px !important;
  max-width: calc(100% - 2rem);
  top: 20vh;

  .cogs-modal-content {
    padding: 1.5rem;
  }

  .cogs-icon-Close {
    cursor: pointer;
  }
`;
